// src/Services/pdf/validateModel.js
// Dev-time validator for DocumentModel using JSON Schema (AJV). No-op in production builds.

let compiled = null;

/**
 * Validate a DocumentModel in development; noop in production.
 * If AJV is not available, performs a minimal structural check to ensure `content` is an array.
 * @param {object} model
 * @returns {Promise<void>} rejects with Error in dev if invalid
 */
export async function validateDocumentModel(model) {
  const debug = (() => { try { return typeof window !== 'undefined' ? (window.PDF_DEBUG !== false) : false; } catch (_) { return false; }})();
  if (debug) console.log('[PDF][validate] start', { hasContent: Array.isArray(model?.content), contentLength: model?.content?.length });
  try {
    if (process && process.env && process.env.NODE_ENV === 'production') return;
  } catch (_) {
    // If process not available (e.g., some runtimes), still attempt validation.
  }

  // Lazy import schema and AJV only in dev to keep bundle size small
  if (!compiled) {
    try {
      const schema = (await import(/* webpackChunkName: "pdf-schema" */ '@/Services/pdf/schemas/documentModel.schema.json')).default;
      // Prefer Ajv v8 if installed; fall back to minimal check if not present
      let AjvCtor = null;
      try {
        AjvCtor = (await import(/* webpackChunkName: "ajv" */ 'ajv')).default || (await import('ajv'));
      } catch (_) {}
      if (AjvCtor) {
        const ajv = new AjvCtor({ allErrors: true, strict: false });
        compiled = ajv.compile(schema);
        if (debug) console.log('[PDF][validate] AJV ready');
      } else {
        // Minimal validator: ensure object and content is an array of acceptable blocks
        compiled = (data) => {
          if (!(data && typeof data === 'object' && Array.isArray(data.content))) return false;
          const okBlock = (b) => {
            if (b == null) return false;
            if (typeof b === 'string') return true;
            if (typeof b !== 'object') return false;
            // Accept alias blocks with type or native pdfmake nodes with known keys
            return (
              b.type !== undefined ||
              b.table !== undefined ||
              b.columns !== undefined ||
              b.stack !== undefined ||
              b.text !== undefined ||
              b.image !== undefined ||
              b.qr !== undefined ||
              b.pageBreak !== undefined
            );
          };
          return data.content.every(okBlock);
        };
        compiled.errors = [];
        if (debug) console.log('[PDF][validate] Using minimal validator');
      }
    } catch (e) {
      // If schema import fails for any reason, use the minimal validator above
      compiled = (data) => {
        if (!(data && typeof data === 'object' && Array.isArray(data.content))) return false;
        const okBlock = (b) => {
          if (b == null) return false;
          if (typeof b === 'string') return true;
          if (typeof b !== 'object') return false;
          return (
            b.type !== undefined || b.table !== undefined || b.columns !== undefined || b.stack !== undefined ||
            b.text !== undefined || b.image !== undefined || b.qr !== undefined || b.pageBreak !== undefined
          );
        };
        return data.content.every(okBlock);
      };
      compiled.errors = [];
      if (debug) console.warn('[PDF][validate] Schema import failed; using minimal validator');
    }
  }

  const valid = compiled(model);
  if (!valid) {
    const msgs = (compiled.errors || []).map(e => `${e.instancePath || e.dataPath || ''} ${e.message || ''}`.trim()).filter(Boolean);
    if (debug) console.error('[PDF][validate] invalid', msgs);
    throw new Error(`Invalid DocumentModel: ${msgs.join('; ') || 'Schema validation failed'}`);
  }
  if (debug) console.log('[PDF][validate] ok');
}

export default validateDocumentModel;
